import React, { useEffect, useState, useContext, useCallback } from "react";
import secureAxios from "../../utils/secureAxios";
import { formatPhone } from "../../utils/formatPhone";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";

const ConsumerList = ({ onEdit, refreshTrigger }) => {
  const { user } = useContext(AuthContext);
  const [consumers, setConsumers] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [selectedCounselor, setSelectedCounselor] = useState("");
  const [searchName, setSearchName] = useState("");
  const [debouncedSearchName, setDebouncedSearchName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();

  // Load counselors for dropdown
  useEffect(() => {
    if (!user?.tenant_id) return;

    secureAxios
      .get(`${apiBase}/api/counselors`, {
        params: { tenant_id: user.tenant_id },
      })
      .then((res) => setCounselors(res.data))
      .catch((err) => {
        console.error("Error loading counselors:", err);
        setError("Failed to load counselors");
      });
  }, [user?.tenant_id, apiBase]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchName(searchName);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchName]);

  // Load filtered consumers
  useEffect(() => {
    if (!user?.tenant_id || !user?.user_id || !user?.role) return;

    const fetchConsumers = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          tenant_id: user.tenant_id,
          user_id: user.user_id,
          role: user.role,
          tenant_type: user.tenant_type || "vendor",
        };

        if (selectedCounselor) {
          params.counselor_id = selectedCounselor;
        }
        // Only add search parameter if there's actual content (not just spaces)
        const trimmedSearch = debouncedSearchName.trim();
        if (trimmedSearch) {
          params.search_name = trimmedSearch;
        }

        const response = await secureAxios.get(`${apiBase}/api/consumers`, {
          params,
        });
        setConsumers(response.data);
      } catch (err) {
        console.error("Error loading consumers:", err);
        setError("Failed to load consumers. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchConsumers();
  }, [user, selectedCounselor, debouncedSearchName, refreshTrigger, apiBase]);

  const handleDelete = async (consumerId, consumerName) => {
    if (
      !window.confirm(
        `Are you sure you want to archive ${consumerName}?\n\n` +
          `This action will mark the consumer as inactive.\n` +
          `All data will be preserved for HIPAA compliance.`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(`${apiBase}/api/consumers/${consumerId}`, {
        params: { tenant_id: user.tenant_id },
      });

      // Remove from local state for immediate UI update
      setConsumers((prev) => prev.filter((c) => c.consumer_id !== consumerId));
      alert("✅ Consumer archived successfully!");
    } catch (err) {
      console.error("❌ Failed to delete consumer:", err);
      alert(err.response?.data?.error || "Failed to archive consumer");
    }
  };

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchName(e.target.value);
  }, []);

  // Handle counselor filter change
  const handleCounselorChange = useCallback((e) => {
    setSelectedCounselor(e.target.value);
  }, []);

  if (loading && !consumers.length && !searchName) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading consumers...</span>
        </div>
      </div>
    );
  }

  if (error && !consumers.length) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button
          className="btn btn-sm btn-outline-danger ms-3"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Consumers</h2>
        <span className="text-muted">Total: {consumers.length} consumers</span>
      </div>

      {/* PHI Notice */}
      <div className="alert alert-warning mb-3" role="alert">
        <i className="bi bi-shield-lock"></i>{" "}
        <strong>Protected Health Information (PHI):</strong>
        <br />
        This section contains sensitive consumer data including date of birth
        and health information. All access is logged for HIPAA compliance.
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Filter by Counselor:</label>
          <select
            className="form-select"
            value={selectedCounselor}
            onChange={handleCounselorChange}
            disabled={loading}
          >
            <option value="">All Counselors</option>
            {counselors.map((c) => (
              <option key={c.counselor_id} value={c.counselor_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label" htmlFor="consumer-search-input">
            Search by Name:
          </label>
          <div className="input-group">
            <input
              id="consumer-search-input"
              type="text"
              className="form-control"
              placeholder="Enter consumer name..."
              value={searchName}
              onChange={handleSearchChange}
              autoComplete="off"
            />
            {searchName && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchName("")}
                title="Clear search"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
          {searchName && searchName !== debouncedSearchName && (
            <small className="text-muted">Searching...</small>
          )}
        </div>
      </div>

      {/* Desktop/tablet view */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Counselor</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Case ID</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {consumers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  {searchName || selectedCounselor
                    ? "No consumers found matching your criteria."
                    : "No consumers found."}
                </td>
              </tr>
            ) : (
              consumers.map((c) => (
                <tr key={c.consumer_id}>
                  <td>
                    <strong>{c.name}</strong>
                    {c.date_of_birth && (
                      <i
                        className="bi bi-shield-lock text-muted ms-2"
                        title="PHI"
                      ></i>
                    )}
                  </td>
                  <td>{c.counselor_name || "—"}</td>
                  <td>{formatPhone(c.phone_number) || "—"}</td>
                  <td>{c.email || "—"}</td>
                  <td>{c.case_id || "—"}</td>
                  <td>
                    <span
                      className={`badge ${
                        c.status === "Active" ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {c.status || "Unknown"}
                    </span>
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => onEdit(c)}
                      title="Edit consumer"
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(c.consumer_id, c.name)}
                      title="Archive consumer"
                    >
                      <i className="bi bi-archive"></i> Archive
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile-friendly card view */}
      <div className="d-block d-md-none">
        {consumers.length === 0 ? (
          <div className="alert alert-info">
            {searchName || selectedCounselor
              ? "No consumers found matching your criteria."
              : "No consumers found."}
          </div>
        ) : (
          consumers.map((c) => (
            <div key={c.consumer_id} className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">
                  {c.name}
                  {c.date_of_birth && (
                    <i
                      className="bi bi-shield-lock text-muted ms-2"
                      title="PHI"
                    ></i>
                  )}
                  <span
                    className={`badge ${
                      c.status === "Active" ? "bg-success" : "bg-secondary"
                    } ms-2`}
                  >
                    {c.status || "Unknown"}
                  </span>
                </h5>
                <div className="mb-2">
                  <strong>Counselor:</strong> {c.counselor_name || "—"}
                  <br />
                  <strong>Phone:</strong> {formatPhone(c.phone_number) || "—"}
                  <br />
                  <strong>Email:</strong> {c.email || "—"}
                  <br />
                  <strong>Case ID:</strong> {c.case_id || "—"}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => onEdit(c)}
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(c.consumer_id, c.name)}
                  >
                    <i className="bi bi-archive"></i> Archive
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* HIPAA Compliance Footer */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-info-circle"></i> Consumer records contain
          Protected Health Information (PHI) including date of birth, case
          notes, and health status. All access is logged with 7-year retention
          per HIPAA requirements. Only authorized personnel with a legitimate
          need should access this data.
        </small>
      </div>
    </div>
  );
};

export default ConsumerList;
